import React, { useEffect, useState } from 'react'
import moment from 'moment'
import API from '../../services/API'
import AnDateTimePicker from '../../components/DateTime/DateTimePicker'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

// Nightly + retry DBS Update Service run history: the durable record of
// whether the DBS API was reachable, since the per-certificate results are
// overwritten by the next run.
const DBSRunLogs = () => {
  const [logs, setLogs] = useState(null)

  useEffect(() => {
    API.get('/admincrm/dbs-run-logs').then((res) => {
      if (res.status === 200) setLogs(res.data)
    })
  }, [])

  return (
    <Card>
      <CardHeader
        title="DBS Update Service Runs"
        subheader="03:00 nightly sweep plus a 07:00 retry of failed checks. Failures usually mean the DBS API was unreachable or in maintenance."
      />
      <CardContent>
        {!logs?.length ? (
          <Typography color="text.secondary">
            No runs recorded yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Started</TableCell>
                  <TableCell>Trigger</TableCell>
                  <TableCell align="right">Due</TableCell>
                  <TableCell align="right">OK</TableCell>
                  <TableCell align="right">No match</TableCell>
                  <TableCell align="right">New info</TableCell>
                  <TableCell align="right">Missing data</TableCell>
                  <TableCell align="right">Failed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => {
                  const failedTenants = (log.tenants ?? []).filter(
                    (tenant) => tenant.error || (tenant.failed ?? 0) > 0,
                  )
                  return (
                    <TableRow key={log._id}>
                      <TableCell>
                        {moment(log.startedAt).format('DD/MM/YYYY HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={log.trigger}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {log.totals?.eligible ?? 0}
                      </TableCell>
                      <TableCell align="right">{log.totals?.ok ?? 0}</TableCell>
                      <TableCell align="right">
                        {log.totals?.noMatch ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {log.totals?.newInfo ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {log.totals?.missingData ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {failedTenants.length ? (
                          <Tooltip
                            arrow
                            title={failedTenants
                              .map((tenant) =>
                                tenant.error
                                  ? `${tenant.subdomain}: run errored`
                                  : `${tenant.subdomain}: ${tenant.failed} failed`,
                              )
                              .join(', ')}
                          >
                            <Chip
                              size="small"
                              color="error"
                              label={log.totals?.failed ?? 0}
                            />
                          </Tooltip>
                        ) : (
                          (log.totals?.failed ?? 0)
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}

const Maintenance = () => {
  const [emergencyMaintenance, setEmergencyMaintenance] = useState(false)
  const [plannedMaintenanceStart, setPlannedMaintenanceStart] = useState(null)
  const [plannedMaintenanceEnd, setPlannedMaintenanceEnd] = useState(null)
  const [involvesData, setInvolvesData] = useState(false)

  useEffect(() => {
    API.get('/admincrm/maintenance').then((res) => {
      if (res.status === 200) {
        setEmergencyMaintenance(res?.data?.emergencyMaintenance ?? false)
        setPlannedMaintenanceStart(res?.data?.start ?? null)
        setPlannedMaintenanceEnd(res?.data?.end ?? null)
        setInvolvesData(res?.data?.involvesData ?? false)
      }
    })
  }, [])

  const submitMaintenanceChanges = () => {
    API.put('/admincrm/maintenance', {
      involvesData,
      emergencyMaintenance,
      start: plannedMaintenanceStart,
      end: plannedMaintenanceEnd,
    }).then((res) => {
      res.status === 200 && window.location.reload()
    })
  }

  return (
    <Stack gap={2}>
      <Card>
        <CardHeader title="Maintenance" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <AnDateTimePicker
              label="Planned Maintenance Start"
              date={plannedMaintenanceStart}
              onChange={(newDate) => setPlannedMaintenanceStart(newDate)}
              fullWidth
              disabled={emergencyMaintenance}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <AnDateTimePicker
              label="Planned Maintenance End"
              date={plannedMaintenanceEnd}
              onChange={(newDate) => setPlannedMaintenanceEnd(newDate)}
              fullWidth
              disabled={emergencyMaintenance}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Stack alignItems="center" direction="row" gap={1}>
              <Switch
                checked={emergencyMaintenance}
                color="error"
                onChange={(e) => setEmergencyMaintenance(e.target.checked)}
              />
              <Typography>Emergency Maintenance Mode</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Stack alignItems="center" direction="row" gap={1}>
              <Switch
                checked={involvesData}
                color="error"
                onChange={(e) => setInvolvesData(e.target.checked)}
              />
              <Typography>Involves Data</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              onClick={() => submitMaintenanceChanges()}
              color="anGreen"
              variant="contained"
            >
              Submit Changes
            </Button>
          </Grid>
        </Grid>
      </CardContent>
      </Card>
      <DBSRunLogs />
    </Stack>
  )
}

export default Maintenance
